import React, { FC, useCallback, useEffect, useRef } from 'react';
import { buildEpisodeTitle, getBroadcastStreamUseCase } from 'utils/fnData';
import { getErrorStatus, isSessionListingForbidden } from 'utils/fnError';
import { ContentUnavailableAlert } from 'components/ContentUnavailableAlert/ContentUnavailableAlert';
import GSApi from 'api/Api';
import { usePlayer } from '../../../providers/player/PlayerContext';
import { PlayerWrapper } from '../../Player/PlayerWrapper';
import { getLivePlayerURL } from '../../../utils/fnUrl';
import { useDataFetcher } from '../../../hooks/useDataFetcher/useDataFetcher';
import { BroadcastPlayerAsset, PlayerEvents, PlayingAssetType } from '../../../types/Player';
import { PageErrorNotFound } from '../PageErrorNotFound/PageErrorNotFound';
import { useAuth } from '../../../providers/useAuth/AuthContext';
import { FetchPlayingAsset, FetchParamById, StreamIDKey } from '../../../types/ApiTypes';
import { useGlobalNetworkError, useGlobalNoInternet, withNetworkCheck } from '../../../hooks/withNetworkCheck/withNetworkCheck';
import { HelmetWrapper } from '../../HelmetWrapper/HelmetWrapper';
import { getPlayerPageHelmetData } from '../../../utils/fnHelmet';
import { withPlayer } from '../../../hooks/withPlayer/withPlayer';
import { usePersistentState } from '../../../hooks/useStorage/useStorage';
import { useRecording } from '../../../providers/useRecording/RecordingContext';
import { RecordingEvent } from '../../../types/RecordingTypes';
import { useNavigation } from '../../../hooks/useNavigation';
import CastHandler from '../../../providers/cast/CastHandler';
import PlaybackManager from '../../../providers/player/PlaybackManager';
import PageTakeover from '../Settings/PageTakeover/PageTakeover';

const CatchupPlayerPage: FC<{ broadcastId: string }> = ({ broadcastId }) => {
    const { onNoInternet } = useGlobalNoInternet();
    const { onNetworkError } = useGlobalNetworkError();

    const { navigate, onBackPressed } = useNavigation();

    const { response: listingDetails, fetcher: listingDetailsFetcher } = useDataFetcher<any, FetchParamById>(
        (param) => GSApi.fetchListing(param.id),
        onNoInternet,
        onNetworkError
    );

    const { response, error, loading, fetcher, reset } = useDataFetcher<any, FetchPlayingAsset>(
        () =>
            GSApi.createStreamSession(
                getBroadcastStreamUseCase((listingDetails.start + listingDetails.duration) * 1000),
                StreamIDKey.LISTING_ID,
                broadcastId,
                {},
                (res) => res
            ),
        onNoInternet,
        onNetworkError
    );

    const sessionCreated = useRef<boolean>(false);
    const { store: playbackPositions } = usePersistentState('playbackPositions', {});

    const { isGuest, openGuestAlert, isSignedOutNotice } = useAuth();
    const {
        asset: currentPlayerAsset,
        setAsset,
        addEventListener,
        removeEventListener,
        resetPlayerAsset,
        isMini,
        closeMiniPlayer,
    } = usePlayer();
    const { addEventListener: addRecordingEventListener, removeEventListener: removeRecordingEventListener } = useRecording();

    const fetchDetails = () => {
        if (!isGuest) {
            listingDetailsFetcher({ id: broadcastId });
        }
    };

    const setNotRecorded = () => {
        setAsset({
            ...currentPlayerAsset,
            ...{
                isRecorded: false,
            },
        });
    };

    const onStreamEndReached = () => {
        if (isMini) {
            closeMiniPlayer();
        } else {
            onBackPressed();
        }
    };

    const onJumpToLiveRequested = useCallback(() => {
        const channelId = listingDetails?.channel_id;
        if (channelId) {
            navigate(getLivePlayerURL(channelId, { live: true }), { replace: true });
        }
    }, [navigate, listingDetails?.channel_id]);

    useEffect(() => {
        resetPlayerAsset();
        reset();

        if (CastHandler.isCast()) {
            // Returning to the player while casting — restore the casting asset for display instead of creating a new session.
            const castAsset = PlaybackManager.getPlayingAsset();
            if (castAsset) {
                sessionCreated.current = true;
                setAsset(castAsset);
            }
        } else {
            sessionCreated.current = false;
        }

        fetchDetails();
    }, [broadcastId]);

    useEffect(() => {
        if (!listingDetails || CastHandler.isCast()) {
            return;
        }

        fetcher(
            {
                id: broadcastId,
                createSession: !sessionCreated.current,
            },
            true
        );
    }, [listingDetails]);

    useEffect(() => {
        if (response && listingDetails) {
            let assetToSet: BroadcastPlayerAsset = response;
            if (!sessionCreated.current) {
                sessionCreated.current = true;
                PlaybackManager.startHeartbeat(response.id, response.heartbeat_interval);
                const { duration, start, program } = listingDetails;
                assetToSet = {
                    ...response,
                    use_case: PlayingAssetType.REPLAY,
                    listingId: broadcastId,
                    bookmark: playbackPositions?.listing_playback_positions?.[response?.listing_id] || 0,
                    duration,
                    startTime: start * 1000,
                    endTime: (start + duration) * 1000,
                    title: listingDetails.program?.title,
                    image: listingDetails?.program?.image_url || listingDetails?.image_url,
                    subtitle: buildEpisodeTitle(
                        listingDetails?.program?.episode_title || listingDetails?.program?.episode_title_original,
                        listingDetails?.program?.season_number,
                        listingDetails?.program?.episode_number
                    ),
                    description: listingDetails?.program?.description,
                    cast: listingDetails?.program?.credits,
                    genres: listingDetails?.program?.genres?.map((g: string) => ({ Value: g })),
                    releaseYear: listingDetails?.program?.release_year,
                    countries: listingDetails?.program?.production_countries,
                    episode_title: program?.episode_title || program?.episode_title_original,
                    seriesId: listingDetails?.program?.program_series_id,
                };
            } else {
                assetToSet = {
                    ...currentPlayerAsset,
                    ...response,
                    use_case: PlayingAssetType.REPLAY,
                    manifestUrl: currentPlayerAsset?.manifestUrl,
                };
            }
            setAsset(assetToSet);

            addEventListener(PlayerEvents.JUMP_TO_LIVE, onJumpToLiveRequested);

            return () => {
                removeEventListener(PlayerEvents.JUMP_TO_LIVE, onJumpToLiveRequested);
            };
        }

        return undefined;
    }, [response, listingDetails, onJumpToLiveRequested]);

    useEffect(() => {
        if (response) {
            addEventListener(PlayerEvents.END_OF_STREAM, onStreamEndReached);
        }

        return () => {
            removeEventListener(PlayerEvents.END_OF_STREAM, onStreamEndReached);
        };
    }, [response, isMini]);

    useEffect(() => {
        if (isGuest) {
            openGuestAlert('/');
        }
    }, [broadcastId]);

    useEffect(() => {
        addRecordingEventListener(RecordingEvent.DELETE, setNotRecorded);
        return () => removeRecordingEventListener(RecordingEvent.DELETE, setNotRecorded);
    }, [currentPlayerAsset]);

    if (error && !isSignedOutNotice) {
        if (getErrorStatus(error) === 409) {
            return (
                <PageTakeover
                    limitReached
                    onSessionDeleted={() => {
                        reset();
                        fetcher(null);
                    }}
                />
            );
        }

        if (isSessionListingForbidden(error)) {
            return <ContentUnavailableAlert onClose={() => onBackPressed()} />;
        }

        return <PageErrorNotFound onButtonClick={closeMiniPlayer} />;
    }

    return (
        <>
            {response && <HelmetWrapper {...getPlayerPageHelmetData(listingDetails)} />}
            {<PlayerWrapper loading={loading} />}
        </>
    );
};

export default withNetworkCheck(withPlayer(CatchupPlayerPage));
